import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { RoutineContext } from './contexts/RoutineContext';
import { UserContext } from './contexts/UserContext'
import { UIContext } from './contexts/UIContext'
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import axios from 'axios';

import './App.css';
import badgeLight from './assets/MS_Startups_Celebration_Badge_Light.png';
import badgeDark from './assets/MS_Startups_Celebration_Badge_Dark.png';


import Login from './pages/Login/Login';
import Signup from './pages/SignUp/SignUp';
import NavBar from './components/NavBar/NavBar';
import WelcomePage from './pages/WelcomePage/WelcomePage';
import InterestsPage from './pages/InterestsPage/InterestsPage';
import GeneratedStryds from './pages/GeneratedStryd/GeneratedStryds';
import RoutinePage from './pages/RoutinePage/RoutinePage'
import ChoicePage from './pages/ChoicePage/ChoicePage';
import AddRoutinePage from './pages/AddRoutinePage/AddRoutinePage';
import PlanBuilderPage from './pages/PlanBuilderPage/PlanBuilderPage';
import GeneratedPlanPage from './pages/GeneratedPlanPage/GeneratedPlanPage';
import SavedPlanPage from './pages/SavedPlanPage/SavedPlanPage';
import ExploreRoutines from './pages/ExploreRoutines/ExploreRoutines';
import People from './pages/People/People';
import UserProfile from './pages/UserProfile/UserProfile';
import UpgradePage from './pages/UpgradePage/UpgardePage';
import MealPlans from './pages/MealPlans/MealPlans';
import ScrollToTop from './utils/scrollToTop';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BottomNav from './components/BottomNav/BottomNav';
import SEO from './components/SEO/SEO';
import { routesSeo } from './config/routesSeo';

import {
	BrowserRouter as Router,
	Link,
	Routes,
	Route,
	useLocation,
} from 'react-router-dom';

import ProfilePage from './pages/ProfilePage/ProfilePage';
import PlanConfirmation from './pages/PlanConfirmation/PlanConfirmation';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import CaliChatBot from './pages/CaliChatBot/CaliChatBot';
import AuthCallback from './pages/AuthCallback/AuthCallback';
import Feed from './pages/Feed/Feed';
import PostDetail from './pages/PostDetail/PostDetail';
import Search from './pages/Search/Search';
import ChatPage from './pages/ChatPage/ChatPage';
import Notifications from './pages/Notifications/Notifications';
import NanoTestPage from './pages/NanoTestPage/NanoTestPage';
import RootLandingHome from './components/RootLandingHome/RootLandingHome';
import OnboardingIntro from './pages/OnboardingFlow/OnboardingIntro';
import OnboardingPersonalization from './pages/OnboardingFlow/OnboardingPersonalization';
import OnboardingPreview from './pages/OnboardingFlow/OnboardingPreview';
import OnboardingPaymentSuccess from './pages/OnboardingFlow/OnboardingPaymentSuccess';
import RoutineRouteResolverPage from './pages/OnboardingFlow/RoutineRouteResolverPage';
import PrivacyPage from './pages/LegalPage/PrivacyPage';
import TermsPage from './pages/LegalPage/TermsPage';
import { initializeOfflineMutationQueue } from './utils/offlineMutationQueue';
import { useAuthUser } from './hooks/useAuthUser';
import BrowserNotificationBridge from './components/BrowserNotificationBridge/BrowserNotificationBridge';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import PremiumRoute from './components/PremiumRoute/PremiumRoute';
import { requestWithApiFallback, buildApiBases } from './utils/apiRequest';
import { normalizeUserPayload } from './utils/userData';
import { useAuth } from '@clerk/clerk-react';
import { AppAuthContext } from './contexts/AppAuthContext';
import { writeSessionAuthHint } from './utils/sessionAuthHint';
import { notifyNativeWebViewContentReady } from './utils/nativeWebViewBridge';

const initialOptions = {
	clientId: process.env.REACT_APP_PAYPALID,
	vault: true,
	intent: "subscription"
};

const AppContent = () => {
	const { user, isLoaded } = useAuthUser();
	const { getToken } = useAuth();
	const { testAuthSession } = useContext(AppAuthContext);
	const { isDarkMode } = useTheme();
	const API_URL = process.env.REACT_APP_API_URL;
	const deferPayPal = !process.env.REACT_APP_PAYPALID;
	const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
	const [queuedMutationCount, setQueuedMutationCount] = useState(0);
	const [isSyncingQueuedMutations, setIsSyncingQueuedMutations] = useState(false);
	const [showOfflineNoticeVisible, setShowOfflineNoticeVisible] = useState(false);
	const offlineNoticeTimerRef = useRef(null);
	const lastProfileRefreshRef = useRef(0);

	useEffect(() => {
		notifyNativeWebViewContentReady();
	}, []);

	useEffect(() => {
		if (isLoaded) notifyNativeWebViewContentReady();
	}, [isLoaded]);

	const [currentXP, setCurrentXP] = useState(0);
	const [currentProgress, setCurrentProgress] = useState(0);
	const [dbUser, setDbUser] = useState({})
	const [isDbUserResolved, setIsDbUserResolved] = useState(false);
	const [totalXP, setTotalXP] = useState(0)
	const [globalUIState, setGlobalUIState] = useState({
		screenWidth: "none",
		currentSection: "app", // Fixed typo in `currentSection`
	});

	const deriveFallbackUsername = useCallback(() => {
		const fromUsername = String(user?.username || '').trim();
		if (fromUsername) return fromUsername;
		const fromFirstName = String(user?.firstName || '').trim();
		if (fromFirstName) return fromFirstName;
		const fromFullName = String(user?.fullName || '').trim();
		if (fromFullName) return fromFullName;
		const primaryEmail = String(user?.emailAddresses?.[0]?.emailAddress || '').trim();
		if (primaryEmail) return primaryEmail.split('@')[0];
		return `athlete_${String(user?.id || '').slice(-6) || 'new'}`;
	}, [user?.emailAddresses, user?.firstName, user?.fullName, user?.id, user?.username]);

	const getAuthRequestConfig = useCallback(async () => {
		let token = testAuthSession?.token || '';
		if (!token && typeof getToken === 'function') {
			try {
				token = await getToken();
			} catch (_) {
				token = '';
			}
		}
		return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
	}, [getToken, testAuthSession?.token]);

	const ensureUserProfileOnLogin = useCallback(async () => {
		if (!user?.id) return null;
		const email = String(user?.emailAddresses?.[0]?.emailAddress || '').trim();
		if (!email) return null;
		const payload = {
			email,
			username: deriveFallbackUsername(),
			profileImageLink: user?.profileImageUrl || ''
		};
		const authConfig = await getAuthRequestConfig();
		const res = await requestWithApiFallback(API_URL, (base) => axios.post(
			`${base}/users/ensure-profile/${user.id}`,
			payload,
			authConfig
		));
		return normalizeUserPayload(res?.data?.user || res?.data);
	}, [API_URL, deriveFallbackUsername, getAuthRequestConfig, user?.emailAddresses, user?.id, user?.profileImageUrl]);

	const createUserProfileFallback = useCallback(async () => {
		if (!user?.id) return null;
		const email = String(user?.emailAddresses?.[0]?.emailAddress || '').trim();
		if (!email) return null;
		const payload = {
			email,
			username: deriveFallbackUsername(),
			userID: user.id,
			profileImageLink: user?.profileImageUrl || '',
			userPlan: 'calcium-free',
			interests: {}
		};
		const res = await requestWithApiFallback(API_URL, (base) => axios.post(`${base}/users`, payload));
		return normalizeUserPayload(res?.data);
	}, [API_URL, deriveFallbackUsername, user?.emailAddresses, user?.id, user?.profileImageUrl]);

	const getUser = useCallback(async () => {
		if (!user?.id) {
			setDbUser({});
			setIsDbUserResolved(true);
			return;
		}

		setIsDbUserResolved(false);
		try {
			try {
				await ensureUserProfileOnLogin();
			} catch (ensureError) {
				if (process.env.NODE_ENV === 'development') {
					console.log('Non-fatal ensure-profile warning:', ensureError?.message || ensureError);
				}
			}
			const res = await requestWithApiFallback(API_URL, (base) => axios.get(`${base}/users/${user.id}`));
			const userData = normalizeUserPayload(res.data);
			setDbUser(userData || {});
		} catch (err) {
			const status = err?.response?.status;
			if (status === 400 || status === 404) {
				try {
					await ensureUserProfileOnLogin();
					const retryRes = await requestWithApiFallback(API_URL, (base) => axios.get(`${base}/users/${user.id}`));
					const retryUserData = normalizeUserPayload(retryRes.data);
					setDbUser(retryUserData || {});
					return;
				} catch (bootstrapError) {
					const bootstrapStatus = bootstrapError?.response?.status;
					if (bootstrapStatus === 401 || bootstrapStatus === 403) {
						try {
							await createUserProfileFallback();
							const retryRes = await requestWithApiFallback(API_URL, (base) => axios.get(`${base}/users/${user.id}`));
							const retryUserData = normalizeUserPayload(retryRes.data);
							setDbUser(retryUserData || {});
							return;
						} catch (legacyBootstrapError) {
							console.error('Fallback profile bootstrap failed:', legacyBootstrapError?.message || legacyBootstrapError);
						}
					}
					console.error('Failed to bootstrap user profile:', bootstrapError?.message || bootstrapError);
				}
			}
			console.error('Failed to fetch user:', err?.message || err);
			setDbUser((prev) => {
				// Preserve last known-good user state on transient errors.
				if (prev && Object.keys(prev).length > 0) return prev;
				return {};
			});
		} finally {
			setIsDbUserResolved(true);
		}
	}, [API_URL, createUserProfileFallback, ensureUserProfileOnLogin, user?.id]);

	useEffect(() => {
		// Function to update screen width
		const updateScreenWidth = () => {
			setGlobalUIState((prevState) => ({
				...prevState,
				screenWidth: window.innerWidth,
			}));
		};

		// Function to set the initial section to "app"
		const initializeCurrentSection = () => {
			setGlobalUIState((prevState) => ({
				...prevState,
				currentSection: "app",
			}));
		};

		// Set the initial screen width and current section
		updateScreenWidth();
		initializeCurrentSection();

		// Add event listener for window resize
		window.addEventListener("resize", updateScreenWidth);

		// Cleanup event listener on component unmount
		return () => {
			window.removeEventListener("resize", updateScreenWidth);
		};
	}, []);

	useEffect(() => {
		if (!isLoaded) {
			return;
		}
		if (user) {
			getUser();
		} else {
			setDbUser({});
			setIsDbUserResolved(true);
		}
	}, [user, getUser, isLoaded]);

	useEffect(() => {
		if (!isLoaded) {
			return;
		}
		writeSessionAuthHint(Boolean(user));
	}, [isLoaded, user]);

	useEffect(() => {
		if (!user?.id || !isLoaded) return;

		const MIN_REFRESH_INTERVAL_MS = 60 * 1000;
		const refreshIfDue = () => {
			const now = Date.now();
			if (now - lastProfileRefreshRef.current < MIN_REFRESH_INTERVAL_MS) {
				return;
			}
			lastProfileRefreshRef.current = now;
			getUser();
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				refreshIfDue();
			}
		};

		window.addEventListener('focus', refreshIfDue);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			window.removeEventListener('focus', refreshIfDue);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [getUser, isLoaded, user?.id]);

	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	useEffect(() => {
		const apiBases = buildApiBases(API_URL);
		const apiOrigins = apiBases.map((base) => {
			try {
				return new URL(base).origin;
			} catch (_) {
				return null;
			}
		}).filter(Boolean);

		const shouldAttachAuth = (config) => {
			const url = config?.url || '';
			if (!url) return false;
			let originToCheck = null;
			if (url.startsWith('http://') || url.startsWith('https://')) {
				try {
					originToCheck = new URL(url).origin;
				} catch (_) {
					return false;
				}
			} else {
				// Relative URL: use baseURL if set, else same origin
				if (config.baseURL) {
					try {
						originToCheck = new URL(config.baseURL).origin;
					} catch (_) {}
				}
				if (!originToCheck && typeof window !== 'undefined') {
					originToCheck = window.location?.origin;
				}
			}
			return originToCheck ? apiOrigins.includes(originToCheck) : false;
		};

		const requestInterceptorId = axios.interceptors.request.use(async (config) => {
			if (!shouldAttachAuth(config)) {
				return config;
			}

			const nextConfig = {
				...config,
				headers: {
					...(config.headers || {})
				}
			};

			let token = testAuthSession?.token || '';
			if (!token && typeof getToken === 'function') {
				try {
					token = await getToken();
				} catch (err) {
					if (process.env.NODE_ENV === 'development') {
						console.log('[Auth] getToken error', err?.message || err);
					}
					token = '';
				}
			}

			if (token) {
				nextConfig.headers.Authorization = `Bearer ${token}`;
			} else if (nextConfig.headers.Authorization) {
				delete nextConfig.headers.Authorization;
			}

			if (process.env.NODE_ENV === 'development') {
				console.log('[Auth] Request', config.url || config.baseURL, { hasToken: !!token, tokenLength: token?.length });
			}
			return nextConfig;
		});

		return () => {
			axios.interceptors.request.eject(requestInterceptorId);
		};
	}, [API_URL, getToken, testAuthSession?.token]);

	useEffect(() => {
		const teardownQueue = initializeOfflineMutationQueue(axios, {
			apiBaseUrl: API_URL,
			onQueueSizeChange: setQueuedMutationCount,
			onSyncStateChange: setIsSyncingQueuedMutations,
		});
		return () => {
			teardownQueue();
		};
	}, [API_URL]);

	const showConnectionNotice = !isOnline || queuedMutationCount > 0 || isSyncingQueuedMutations;
	const queueLabel = queuedMutationCount === 1 ? 'action' : 'actions';
	const offlineNoticeClassName = `OfflineNotice ${isOnline ? 'OfflineNotice--syncing' : 'OfflineNotice--offline'}`;

	useEffect(() => {
		if (!showConnectionNotice) return;
		setShowOfflineNoticeVisible(true);
		if (offlineNoticeTimerRef.current) clearTimeout(offlineNoticeTimerRef.current);
		offlineNoticeTimerRef.current = setTimeout(() => setShowOfflineNoticeVisible(false), 5000);
		return () => {
			if (offlineNoticeTimerRef.current) {
				clearTimeout(offlineNoticeTimerRef.current);
				offlineNoticeTimerRef.current = null;
			}
		};
	}, [showConnectionNotice]);

	return (
		<div className="App">
			<PayPalScriptProvider options={initialOptions} deferLoading={deferPayPal}>
				{showConnectionNotice && showOfflineNoticeVisible && (
					<div className={offlineNoticeClassName} role='status' aria-live='polite'>
						{!isOnline
							? `You are offline. Cached content is available. ${queuedMutationCount} pending ${queueLabel} will sync when you reconnect.`
							: `Back online. Syncing ${queuedMutationCount} queued ${queueLabel} now.`}
					</div>
				)}
				<div className='pages'>
					<Router>
						{/* Route-aware SEO */}
						<RouteAwareSEO />
						<UIContext.Provider
							value={
								{ globalUIState, setGlobalUIState }
							}>
							<UserContext.Provider value={
								{ dbUser, setDbUser, isDbUserResolved }
							}>
								<ScrollToTop />
								<RoutineContext.Provider value={{
									currentXP,
									setCurrentXP,
									totalXP,
									setTotalXP,
									currentProgress,
									setCurrentProgress
								}}>
									<BrowserNotificationBridge />
									<AppLayoutShell user={user} />
								</RoutineContext.Provider>
							</UserContext.Provider>
						</UIContext.Provider>
					</Router>
				</div>

				<Analytics />
				<ToastContainer
					position="top-right"
					autoClose={5000}
					hideProgressBar={false}
					newestOnTop={false}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme={isDarkMode ? "dark" : "light"}
				/>
			</PayPalScriptProvider>
		</div>
	);
};

function App() {
	return (
		<ErrorBoundary>
			<ThemeProvider>
				<AppContent />
			</ThemeProvider>
		</ErrorBoundary>
	);
}

export default App;

// Small component that reads the current location and applies SEO defaults
function RouteAwareSEO() {
	const { pathname } = useLocation();
	const config = routesSeo[pathname] || {};
	return (
		<SEO
			title={config.title}
			description={config.description}
			path={pathname}
			noIndex={config.noIndex}
		/>
	);
}

const CHROMELESS_PATH_PREFIXES = [
	'/onboarding',
	'/login',
	'/signup',
	'/test-signin',
	'/test-sign-in',
	'/test-signup',
	'/test-sign-up',
	'/auth-callback'
];

const shouldHideChrome = (pathname, isAuthenticated) => {
	if (pathname === '/' && !isAuthenticated) {
		return true;
	}
	return CHROMELESS_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

function AppLayoutShell({ user }) {
	const { pathname } = useLocation();
	const { isDarkMode } = useTheme();
	const hideChrome = shouldHideChrome(pathname, Boolean(user));
	const currentYear = new Date().getFullYear();
	const msBadge = isDarkMode ? badgeDark : badgeLight;

	useEffect(() => {
		document.body.setAttribute('data-chromeless', hideChrome ? 'true' : 'false');
		return () => {
			document.body.removeAttribute('data-chromeless');
		};
	}, [hideChrome]);

	return (
		<>
			{!hideChrome && <NavBar />}
			<ErrorBoundary compact key={pathname}>
				<Routes>
					<Route element={<RootLandingHome />} path='/' />
					<Route element={<WelcomePage />} path='welcome' />
					<Route element={<Login />} path='login' />
					<Route element={<Login />} path='test-signin' />
					<Route element={<Login />} path='test-sign-in' />
					<Route element={<Signup />} path='signup' />
					<Route element={<Signup />} path='test-signup' />
					<Route element={<Signup />} path='test-sign-up' />
					<Route element={<OnboardingIntro />} path='onboarding' />
					<Route element={<OnboardingPersonalization />} path='onboarding/personalize' />
					<Route element={<OnboardingPreview />} path='onboarding/preview/:routineId' />
					<Route element={<OnboardingPaymentSuccess />} path='onboarding/payment-success' />
					<Route element={<InterestsPage />} path='interests' />
					<Route element={<ProtectedRoute><ChoicePage /></ProtectedRoute>} path='choice' />
					<Route element={<GeneratedStryds />} path='generated-stryds' />
					<Route element={<RoutinePage />} path='stryd/:userID/:routineName' />
					<Route element={<RoutineRouteResolverPage />} path='routine/:routineId' />
					<Route element={<ProtectedRoute><AddRoutinePage /></ProtectedRoute>} path='add-routine' />
					<Route element={<ProtectedRoute><PlanBuilderPage /></ProtectedRoute>} path='plan-builder' />
					<Route element={<GeneratedPlanPage />} path='generated-plan' />
					<Route element={<ProtectedRoute><SavedPlanPage /></ProtectedRoute>} path='plan/:planId' />
					<Route element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} path='profile-page' />
					<Route element={<ExploreRoutines />} path='explore-routines' />
					<Route element={<People />} path='people' />
					<Route element={<UserProfile />} path='profile/:userID' />
					<Route element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} path='upgrade' />
					<Route element={<PlanConfirmation />} path='plan-confirm' />
					<Route element={<ProtectedRoute><MealPlans /></ProtectedRoute>} path='meals' />
					<Route
						element={
							<ProtectedRoute>
								<PremiumRoute
									heading='Cali is only available to paid users'
									description='Upgrade to unlock Cali and the rest of Calcium premium.'
								>
									<CaliChatBot />
								</PremiumRoute>
							</ProtectedRoute>
						}
						path='cali-chat'
					/>
					<Route element={<Search />} path='search' />
					<Route element={<Feed />} path='feed' />
					<Route element={<PostDetail />} path='feed/post/:postID' />
					<Route element={<ChatPage />} path='chat' />
					<Route element={<ChatPage />} path='chat/:conversationID' />
					<Route element={<Notifications />} path='notifications' />
					<Route element={<TermsPage />} path='terms' />
					<Route element={<PrivacyPage />} path='privacy' />
					<Route element={<AuthCallback />} path='auth-callback' />
					<Route element={<NanoTestPage />} path='nanotest' />
				</Routes>
			</ErrorBoundary>
			{!hideChrome && <BottomNav />}
			{!hideChrome && (
				<footer className='Footer' role='contentinfo'>
					<div className='FooterInner'>
						<div className='FooterTop'>
							<section className='FooterBrand' aria-label='Calcium brand'>
								<span className='FooterProduct'>Calcium</span>
								<p className='FooterTagline'>Create, track, and share workout routines.</p>
							</section>
							<nav className='FooterNav' aria-label='Footer links'>
								<div className='FooterNavBlock'>
									<span className='FooterNavLabel'>Contact</span>
									<a href='mailto:hello@calcium.fitness' className='FooterLink'>hello@calcium.fitness</a>
									<a href='mailto:emmanuel@calcium.fitness' className='FooterLink'>emmanuel@calcium.fitness</a>
								</div>
								<div className='FooterNavBlock'>
									<span className='FooterNavLabel'>Company</span>
									<a
										href='https://kin3tic.tech'
										target='_blank'
										rel='noopener noreferrer'
										className='FooterLink'>
										Project Kinetic
									</a>
								</div>
								<div className='FooterNavBlock'>
									<span className='FooterNavLabel'>Legal</span>
									<Link to='/terms' className='FooterLink'>Terms of Service</Link>
									<Link to='/privacy' className='FooterLink'>Privacy Policy</Link>
								</div>
							</nav>
						</div>
						<div className='FooterBottom'>
							<div className='FooterLegal'>
								<span className='FooterDate'>(c) {currentYear}</span>
								<span className='FooterCompany'>Projekt Kinetic. Engima Works.</span>
							</div>
							<a
								href='https://www.microsoft.com/startups'
								target='_blank'
								rel='noopener noreferrer'
								className='FooterBadgeLink'
								aria-label='Microsoft for Startups'>
								<img src={msBadge} alt='Microsoft for Startups badge' className='FooterBadge' />
							</a>
						</div>
					</div>
				</footer>
			)}
		</>
	);
}